import React, { useState, useMemo } from "react";
import Navbar from "./Navbar";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Info,
  Gift,
  LogOut,
  CircleUserRound,
  DatabaseIcon,
  MessageSquareText,
  MessageCircleQuestion,
} from "lucide-react";

const SidebarLink = ({ label, onClick, active = false }) => {
  let Icon = null;

  switch (label) {
    case "My Data":
      Icon = DatabaseIcon;
      break;
    case "My Profile":
      Icon = CircleUserRound;
      break;
    case "Rewards":
      Icon = Gift;
      break;
    case "FAQ & Support":
      Icon = MessageCircleQuestion;
      break;
    case "Contact Us":
      Icon = MessageSquareText;
      break;
    case "About Us":
      Icon = Info;
      break;
    case "Log-Out":
      Icon = LogOut;
      break;
    default:
      Icon = null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-start gap-3 text-left text-base rounded-lg px-3 py-2 transition-colors group ${
        active
          ? "text-crimsonGlory bg-gray-50"
          : "text-gray-700 hover:text-crimsonGlory hover:bg-gray-50"
      }`}
    >
      {Icon && (
        <Icon
          className={`!w-[22px] !h-[22px] transition-colors ${
            active
              ? "text-crimsonGlory"
              : "text-gray-400 group-hover:text-crimsonGlory"
          }`}
        />
      )}
      <span>{label}</span>
    </button>
  );
};

const SidebarContent = ({
  initial,
  displayName,
  email,
  activeLabel,
  onNavigate,
  onLogout,
}) => {
  return (
    <div className="flex flex-col w-full h-full px-6 py-8 justify-between">
      <div className="space-y-6">
        {/* User Info Header */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-crimsonGlory/10 flex items-center justify-center text-crimsonGlory text-2xl font-bold">
            {initial}
          </div>
          <div className="space-y-0.5 max-w-[10rem]">
            <div className="font-semibold text-gray-900 text-sm truncate">
              {displayName || "Guest"}
            </div>
            {email && (
              <div className="text-xs text-gray-500 truncate">{email}</div>
            )}
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="space-y-1.5">
          <SidebarLink
            label="My Data"
            onClick={() => onNavigate("/alarm/verify")}
            active={activeLabel === "My Data"}
          />
          <SidebarLink
            label="My Profile"
            onClick={() => onNavigate("/contact-info")}
            active={activeLabel === "My Profile"}
          />
          <SidebarLink
            label="Rewards"
            onClick={() => onNavigate("/rewards")}
            active={activeLabel === "Rewards"}
          />
          <SidebarLink
            label="Contact Us"
            onClick={() => {
              window.open(
                "https://support.ownli.co/hc/en-us/requests/new",
                "_blank"
              );
            }}
            active={activeLabel === "Contact Us"}
          />
          <SidebarLink label="Log-Out" onClick={onLogout} />
        </nav>
      </div>

      {/* Sidebar Footer */}
      <div className="pt-6 border-t border-gray-100 text-[11px] text-gray-500 space-y-1 flex gap-4 items-center justify-center">
        <img
          alt="Ownli"
          className="w-10 h-10 rounded-full"
          src="https://parked.blob.core.windows.net/ownli/www/logos/logo.png"
        />
        <div>
          <div className="text-black text-xs">Powered by Ownli 2026</div>
          <div className="space-x-1 text-xs">
            <a
              target="_blank"
              rel="noreferrer"
              className="underline text-crimsonGlory"
              href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Use"
            >
              Terms of use
            </a>
            <span className="text-black">&</span>
            <a
              target="_blank"
              rel="noreferrer"
              className="underline text-crimsonGlory"
              href="https://support.ownli.co/hc/en-us/articles/360011276138-Privacy-policy"
            >
              Privacy policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainLayout = ({
  children,
  rootClassName = "",
  footerClassName = "bg-gray-50",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { displayName, email, initial } = useMemo(() => {
    let dName = "";
    let uEmail = "";

    try {
      const raw = sessionStorage.getItem("customerData");
      if (raw) {
        const customer = JSON.parse(raw);
        dName =
          customer.nameFull ||
          `${customer.nameFirst || ""} ${customer.nameLast || ""}`.trim();
        uEmail = customer.contactEmail || customer.loginEmail || "";
      }
    } catch (e) {
      console.error("Failed to parse customerData:", e);
    }

    if (!dName && user) {
      dName =
        user.displayName || user.email || user.phoneNumber || "My Profile";
    }

    if (!uEmail && user) {
      uEmail = user.email || "";
    }

    const init = (dName || "O").trim().charAt(0).toUpperCase();

    return { displayName: dName, email: uEmail, initial: init };
  }, [user]);

  const closeSidebarIfMobile = () => setIsSidebarOpen(false);
  const handleToggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const handleNavigation = (path) => {
    closeSidebarIfMobile();
    navigate(path);
  };

  const handleLogout = async () => {
    setIsSidebarOpen(false);
    try {
      await logout();
    } catch (err) {
      console.error("Error during logout:", err);
    } finally {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/welcome";
    }
  };

  const getActiveLabel = () => {
    const pathname = location.pathname;
    if (pathname.startsWith("/rewards")) return "Rewards";
    if (pathname === "/contact-info") return "My Profile";
    if (pathname === "/contact-us") return "Contact Us";
    return null;
  };

  const activeLabel = getActiveLabel();
  const isAuthenticated = !!user;

  const showSidebar = isAuthenticated && location.pathname !== "/welcome";

  return (
    <div
      className={`min-h-screen h-screen flex flex-col overflow-hidden ${rootClassName}`}
    >
      <Navbar
        isSidebarOpen={showSidebar ? isSidebarOpen : false}
        onToggleSidebar={showSidebar ? handleToggleSidebar : undefined}
      />

      <div className="flex flex-1 overflow-hidden relative border-t-2 border-gray-100">
        {showSidebar && (
          <>
            {/* Sidebar Overlay (mobile + desktop) */}
            {isSidebarOpen && (
              <div className="fixed inset-0 z-40 flex">
                <div className="w-64 bg-white border-r border-gray-100 h-full overflow-y-auto">
                  <SidebarContent
                    email={email}
                    initial={initial}
                    onLogout={handleLogout}
                    displayName={displayName}
                    activeLabel={activeLabel}
                    onNavigate={handleNavigation}
                  />
                </div>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={handleToggleSidebar}
                  className="flex-1 bg-black/30 backdrop-blur-md"
                />
              </div>
            )}
          </>
        )}

        <main className="flex-1 flex flex-col overflow-y-auto bg-mesh grain-overlay">
          <div className="w-full max-w-6xl mx-auto px-4 py-6 relative z-[2]">
            {children}
          </div>
        </main>
      </div>

      <footer
        className={`flex justify-between items-center px-6 py-4 mt-auto text-gray-500 text-sm ${footerClassName}`}
      >
        {/* Left side — Support link */}
        <p className="flex items-center space-x-2 ">
          <span>Need help?</span>
          <button
            onClick={() => {
              window.open(
                "https://support.ownli.co/hc/en-us/requests/new",
                "_blank"
              );
            }}
            className="text-blue-500 hover:text-blue-600 underline"
          >
            Contact Us.
          </button>
        </p>
        {/* Right side — Powered by Ownli */}
        <p className="flex items-center space-x-2">
          <span>Powered by</span>
          <img src="/logo.jpeg" alt="Ownli logo" className="h-4" />
        </p>
      </footer>
    </div>
  );
};

export default MainLayout;
